import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { checkModuleRight, useModuleRights, useUser } from '@work4all/data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';

import { currencyAsSign } from '@work4all/utils';
import { canViewERPPrices } from '@work4all/utils/lib/permissions';

import { CurrencyExchangeInfoContext } from '../../../../containers/mask-overlays/mask-overlay/views/inbound-invoice/currency-exchange-info-context';
import schema from '../../schemata/positions-table-schema.json';

const PRICE_COLUMNS = [
  'bz_Total_AufPerCent',
  'bz_Total_DbPerCent',
  'bz_TotalPurchaseValue',
  'bz_TotalGrossProfit',
  'pos_GrossProfit',
  'pos_Surcharge',
  'pos_DbPerCent',
  'purchasePrice',
];

const DISPOSAL_COLUMNS = [
  'pos_DispositionStart',
  'pos_DispositionStartWeek',
  'bz_DispoFrom',
  'bz_DispoFromWeek',
  'bz_DispoTo',
  'bz_DispoToWeek',
];

const PROJECT_COLUMNS = [
  'bz_ParentProject_Name',
  'bz_ParentProject_Number',
  'bz_Project_Name',
  'bz_Project_Number',
  'bz_HallName',
];

const E_INVOICE_COLUMNS = [
  'bz_EInvoiceBT14',
  'bz_EInvoiceBT13',
  'bz_EInvoiceBT15',
  'bz_EInvoiceBT18',
  'bz_EInvoiceBT11',
  'bz_EInvoiceReverseCharge',
  'bz_EInvoiceBT17',
  'bz_EInvoiceBT16',
  'bz_EInvoiceBT12',
];

const DELIVERY_COLUMNS = [
  'bz_DeliveryAddr_Name',
  'bz_DeliveryAddrContactPersonName',
  'bz_DeliveryAddrCountry',
  'bz_DeliveryAddrText',
];

const BILL_COLUMNS = [
  'bz_BillAddrCompany',
  'bz_BillAddrContactPersonName',
  'bz_BillAddrCountry',
];

const PLANNED_COLUMNS = [
  'factor',
  'deliveryDate',
  'pos_PlanedDeliveryDateCalenderWeek',
];

const CURRENCY_COLUMNS = [
  'bz_GrossTotal',
  'bz_GrossTotalCurrency',
  'bz_Currency',
  'bz_TypeOfPayment',
];

const TOTAL_COLUMNS = [
  'bz_Total_AufPerCent',
  'bz_Total_DbPerCent',
  'bz_TotalPurchaseValue',
  'bz_TotalGrossProfit',
];

const DISPO_COLUMNS = [
  'bz_DispoFrom',
  'bz_DispoFromWeek',
  'bz_DispoTo',
  'bz_DispoToWeek',
];

const ACTUAL_ONGOING_COLUMNS = [
  'bz_ActualDeliveryDate',
  'bz_ActualDeliveryDateWeek',
  'bz_OutgoingDeliveryDate',
  'bz_OutgoingDeliveryDateWeek',
];

const TOUR_COLUMNS = [
  'bz_TourName',
  'bz_TourMo',
  'bz_TourDi',
  'bz_TourMi',
  'bz_TourDo',
  'bz_TourFr',
  'bz_TourSa',
];

const EXCLUDE_BY_TYPE: Partial<Record<Entities, string[]>> = {
  [Entities.calculation]: [
    ...E_INVOICE_COLUMNS,
    ...TOUR_COLUMNS,
    'pos_Article_InventoryAmount',
    'sum_AmountIncomingDeliveryNote',
    'ledgerAccount',
    'pos_AmountBilled',
    'pos_TrackedTime',
    'bz_TotalTrackedTime',
  ],
  [Entities.contract]: [],
  [Entities.offer]: [
    ...TOUR_COLUMNS,
    'pos_Article_InventoryAmount',
    'pos_AmountOrdered',
    'sum_AmountIncomingDeliveryNote',
    'pos_AmountDelivered',
    'pos_AmountBilled',
    'backlog',
    'pos_TrackedTime',
    'bz_TotalTrackedTime',
  ],
  [Entities.invoice]: [
    ...DISPO_COLUMNS,
    ...TOUR_COLUMNS,
    'pos_Article_InventoryAmount',
    'pos_AmountOrdered',
    'pos_DispositionStart',
    'pos_DispositionStartWeek',
    'sum_AmountIncomingDeliveryNote',
    'backlog',
    'pos_TrackedTime',
    'bz_ApprovedInShop',
    'bz_TotalTrackedTime',
    'pos_AmountDelivered',
  ],
  [Entities.deliveryNote]: [
    ...DISPO_COLUMNS,
    'pos_Article_InventoryAmount',
    'pos_AmountOrdered',
    'pos_DispositionStart',
    'pos_DispositionStartWeek',
    'sum_AmountIncomingDeliveryNote',
    'ledgerAccount',
    'backlog',
    'pos_TrackedTime',
    'bz_ApprovedInShop',
    'bz_TotalTrackedTime',
  ],
  [Entities.order]: [
    ...BILL_COLUMNS,
    ...PLANNED_COLUMNS,
    ...TOUR_COLUMNS,
    ...TOTAL_COLUMNS,
    'pos_Surcharge',
    'pos_DbPerCent',
    'sum_AmountIncomingDeliveryNote',
    'ledgerAccount',
    'pos_AmountBilled',
    'pos_GrossProfit',
    'pos_TrackedTime',
    'bz_TotalTrackedTime',
  ],
  [Entities.demand]: [
    ...DISPO_COLUMNS,
    ...TOUR_COLUMNS,
    ...E_INVOICE_COLUMNS,
    ...DELIVERY_COLUMNS,
    ...BILL_COLUMNS,
    ...PLANNED_COLUMNS,
    ...TOTAL_COLUMNS,
    ...ACTUAL_ONGOING_COLUMNS,
    'pos_Article_InventoryAmount',
    'pos_AmountOrdered',
    'pos_Surcharge',
    'pos_DbPerCent',
    'pos_DispositionStart',
    'pos_DispositionStartWeek',
    'sum_AmountIncomingDeliveryNote',
    'ledgerAccount',
    'pos_AmountDelivered',
    'pos_AmountBilled',
    'pos_GrossProfit',
    'pos_TrackedTime',
    'backlog',
    'bz_TotalTrackedTime',
  ],
  [Entities.inboundDeliveryNote]: [
    ...ACTUAL_ONGOING_COLUMNS,
    ...DISPO_COLUMNS,
    ...TOUR_COLUMNS,
    ...E_INVOICE_COLUMNS,
    ...DELIVERY_COLUMNS,
    ...BILL_COLUMNS,
    ...PLANNED_COLUMNS,
    ...CURRENCY_COLUMNS,
    ...TOTAL_COLUMNS,
    'bz_NetValue',
    'bz_NetValueCurrency',
    'pos_Article_InventoryAmount',
    'pos_AmountOrdered',
    'pos_DbPerCent',
    'pos_DispositionStart',
    'pos_DispositionStartWeek',
    'sum_AmountIncomingDeliveryNote',
    'ledgerAccount',
    'pos_AmountBilled',
    'pos_GrossProfit',
    'backlog',
    'pos_TrackedTime',
    'bz_ApprovedInShop',
    'bz_TotalTrackedTime',
  ],
};

const TRACKED_COLUMNS = ['bz_TotalTrackedTime', 'pos_TrackedTime'];

export const useErpPositionSchema = (entity: Entities) => {
  const { t } = useTranslation();
  const currencyExchangeInfo = useContext(CurrencyExchangeInfoContext);
  const defaultCurrency = currencyExchangeInfo?.defaultCurrency;

  const user = useUser();
  const { rights } = useModuleRights();

  return useMemo(() => {
    const exludedColumns = EXCLUDE_BY_TYPE[entity];
    const canSeePrices = canViewERPPrices(user);
    const invoiceModule = checkModuleRight(
      rights,
      ModuleAccessRightType.FEATURE_E_INVOICING
    );
    const projectModule = checkModuleRight(
      rights,
      ModuleAccessRightType.FEATURE_PROJECT
    );
    const disposalModule = checkModuleRight(
      rights,
      ModuleAccessRightType.FEATURE_DISPOSAL
    );

    const timeTrackingModule = checkModuleRight(
      rights,
      ModuleAccessRightType.FEATURE_WORK_TIME_TRACKING
    );
    const shopModule = checkModuleRight(
      rights,
      ModuleAccessRightType.FEATURE_WORK_4_ALL_ONLINE_SHOP
    );
    const projectTimeTrackingModule = checkModuleRight(
      rights,
      ModuleAccessRightType.FEATURE_PROJECT_TIME_TRACKING
    );
    // settings
    // DomainEventPlaner
    return {
      ...schema,
      columns: schema.columns
        .filter((x) => {
          const path0 = x.path[0] as string;

          if (path0?.includes('E_INVOICE')) {
            // if order and demand
            if (!invoiceModule) return false;
            if ([Entities.order, Entities.demand].includes(entity))
              return false;
          }

          if (x.id.includes('bz_ApprovedInShop') && !shopModule) {
            return false;
          }
          if (
            TRACKED_COLUMNS.some((y) => x.id.includes(y)) &&
            !(projectTimeTrackingModule && timeTrackingModule)
          ) {
            return false;
          }

          if (!projectModule && PROJECT_COLUMNS.some((y) => x.id.includes(y))) {
            return false;
          }

          if (
            !disposalModule &&
            DISPOSAL_COLUMNS.some((y) => x.id.includes(y))
          ) {
            return false;
          }

          if (!canSeePrices && PRICE_COLUMNS.some((y) => x.id.includes(y))) {
            return false;
          }

          const parts = x.id.split('.');
          if (parts.some((x) => exludedColumns.includes(x))) return false;

          return true;
        })
        .map((x) => {
          switch (x.id) {
            case 'details.bz_NetValueCurrency':
              if (defaultCurrency?.name)
                x.title = [
                  `${t('COMMON.NET')}, ${currencyAsSign(defaultCurrency.name)}`,
                ];
              break;
            default:
              break;
          }

          return {
            ...x,
            style: x.sticky ? undefined : { background: 'var(--ui03)' },
          };
        }),
    };
  }, [entity, user, rights, defaultCurrency?.name, t]);
};
